/**
 * Shop Settings Service
 * Manages all configurable shop settings stored in Firestore
 * Provides a single source of truth for business configuration
 */

import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { BookingRules, DEFAULT_BOOKING_RULES, mergeBookingRules } from '../shared/bookingRules';
import { SpecialHours } from '../shared/availability';

export interface BusinessHours {
  monday: { open: string; close: string; closed: boolean };
  tuesday: { open: string; close: string; closed: boolean };
  wednesday: { open: string; close: string; closed: boolean };
  thursday: { open: string; close: string; closed: boolean };
  friday: { open: string; close: string; closed: boolean };
  saturday: { open: string; close: string; closed: boolean };
  sunday: { open: string; close: string; closed: boolean };
}

/** Tone applied to a themeable surface — the light/medium/dark matte gradient. */
export type ThemeTone = 'light' | 'medium' | 'dark';

/** Owner-configurable look. Brand hexes retint the app; tones+intensity drive the matte gradients. */
export interface ThemeConfig {
  brand: { primary: string; hero: string; accent: string };
  tones: Record<string, ThemeTone>;   // element key → tone (e.g. { 'cards':'dark','heroes':'light',... })
  intensity: number;                  // 0..1 matte depth
}

export interface ShopSettings {
  // Business Information
  shopName: string;
  shopAddress: string;
  shopPhone: string;
  shopEmail: string;
  shopTimezone: string;

  // Business Hours
  businessHours: BusinessHours;
  // Holiday / special-date overrides (Settings → Special Hours). YYYY-MM-DD → closed | custom hours.
  specialHours?: SpecialHours;

  // Booking Settings
  bookingSettings: {
    minAdvanceBookingMinutes: number; // Min time before booking (e.g., 30 minutes)
    maxAdvanceBookingDays: number; // Max days in advance (e.g., 90)
    defaultAppointmentDuration: number; // Default duration in minutes
    slotInterval: number; // Time slot intervals in minutes (5, 10, 15, 20, 30, 60)
    allowOverlappingBookings: boolean; // For emergency bookings
    requireDepositForNewClients: boolean;
    depositAmount: number;
    emergencyBookingFees: {
      highPriorityFee: number; // Fee for high priority emergency bookings (e.g., 37.50)
      criticalFee: number; // Fee for critical emergency bookings (e.g., 50.00)
    };
  };

  // 🧭 PER-SHOP BOOKING RULES (multi-tenant, Jose 2026-08-07)
  // The booking/timing behavior every shop tunes for itself — back-to-back vs a
  // fixed grid, buffers, the no-show policy, new-client length, no-show block
  // threshold. ONE config the shared brains read (shared/bookingRules.ts). Stored
  // partial; mergeBookingRules() fills defaults. Mirrored to publicConfig/bookingRules
  // so the customer-facing surfaces obey the same rules.
  // BEHAVIOR-PIN: project-slik-pos-booking-rules-config-2026-08-07 / shared/bookingRules.ts
  bookingRules?: Partial<BookingRules>;

  // Calendar Settings
  calendarSettings: {
    startHour: number; // Calendar start hour (0-23)
    endHour: number; // Calendar end hour (0-23)
    showWeekends: boolean;
    defaultView: 'day' | 'week' | 'month';
    showCancelledAppointments: boolean;
  };

  // Notification Settings
  notificationSettings: {
    sendAppointmentReminders: boolean;
    reminderHoursBefore: number; // Hours before appointment
    sendConfirmationEmails: boolean;
    sendCancellationEmails: boolean;
    notifyStaffOfNewBookings: boolean;
  };

  // Payment Settings
  paymentSettings: {
    acceptCash: boolean;
    acceptCard: boolean;
    acceptSLIKCoins: boolean;
    taxRate: number; // Percentage (e.g., 8.5 for 8.5%)
    tipSuggestions: number[]; // Suggested tip percentages [15, 18, 20, 25]
  };

  // 🚫 CANCELLATION & NO-SHOW (Jose 2026-09-07) — per-shop cancel/no-show policy + notifications.
  // The cancel MODAL (reason dropdown + notify toggle + penalty summary) reads these. Whether a late
  // cancel auto-counts as a no-show lives in bookingRules.lateCancelCountsAsNoShow (booking policy).
  cancellationSettings?: {
    // Reasons offered in the cancel modal's dropdown (admin-configurable, like discountReasons).
    reasons?: { id: string; label: string; enabled: boolean; order: number }[];
    // Default state of the "notify client" toggle in the cancel modal (Jose: notify NOT default).
    notifyClientOnCancelDefault: boolean;
  };

  // 🔔 CLIENT NOTIFICATIONS (Jose 2026-09-07) — every client-facing message is a per-shop on/off.
  // "All notifications must be configurable." Sent via email now (live) + SMS when toll-free clears.
  // Cancel-notify is governed by the cancel modal's own toggle (default = cancellationSettings
  // .notifyClientOnCancelDefault), so it isn't duplicated here. This holds the switches with no
  // per-action UI of their own — no-show being the first.
  clientNotifications?: {
    onNoShow: boolean;   // message the client when marked no-show (default on — it's the described behavior)
  };

  // 🎨 THEME (Jose 2026-09-07) — the owner-configurable look (color only). Brand hexes drive the
  // runtime retint (branding.applyTheme); per-element tone (light/medium/dark) + matte intensity
  // drive the matte gradient tokens. Per-shop / multi-tenant; refresh the whole look anytime.
  theme?: ThemeConfig;

  // Staff Settings
  staffSettings: {
    requireBPIMinimum: number; // Minimum BPI for emergency bookings (70)
    autoAssignAppointments: boolean; // Auto-assign based on availability
    showStaffPerformanceToCustomers: boolean;
  };

  // Feature Flags
  features: {
    enableEmergencyBooking: boolean;
    enableAIScheduling: boolean; // Premium AI features
    enableLoyaltyProgram: boolean;
    enableWaitlist: boolean;
    enableRecurringAppointments: boolean;
    enableMultiLocationSupport: boolean;
    enableGiftCards: boolean;
  };

  // Gift Card Settings
  giftCardSettings: {
    enabled: boolean;
    defaultExpirationMonths: number; // Default: 12 months
    allowCustomAmounts: boolean;
    minAmount: number;
    maxAmount: number;
    presetAmounts: number[]; // e.g., [25, 50, 75, 100, 150, 200]
    templates: {
      id: string;
      name: string;
      imageUrl: string;
      description: string;
      season: 'default' | 'holiday' | 'valentines' | 'mothers-day' | 'fathers-day' | 'birthday';
      active: boolean;
    }[];
  };

  // Discount Reasons (Configurable by Admin).
  // Jose 2026-08-09: each discount now carries a TYPE + VALUE so it can be picked from a dropdown and
  // applied without typing an amount. 'comp' = complimentary (100% off / free), 'percent' = value% off,
  // 'fixed' = $value off. `type`/`value` are optional for backward-compat (old configs = reason-only,
  // treated as no auto-amount). The fraud check still runs on the resulting discount at checkout.
  discountReasons?: {
    id: string;
    label: string;
    enabled: boolean;
    order: number;
    type?: 'comp' | 'percent' | 'fixed';
    value?: number; // percent (0-100) when type='percent'; dollars when type='fixed'; ignored for 'comp'
  }[];

  // 📊 REPORTING SETTINGS (for payroll & reports)
  reporting: {
    // Payroll Configuration
    payroll: {
      frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
      // NOTE: the pay-WEEK start lives in commissionPay.payWeekStartDay (default 3 = Wednesday) — the ONE
      // source PayrollReport/GrowthPayWeekCard/barber-profile use. A `weekEndingDay` field used to live
      // here but was DEAD (zero readers) and kept luring fixes to the wrong place — removed 2026-10-27.

      // Commission Structure
      commissionType: 'flat' | 'individual'; // flat = same for all, individual = per barber

      // Flat rate (all barbers same)
      flatCommissionRate: number; // e.g., 60 (barber gets 60%, shop gets 40%)

      // Individual rates (stored per barberId)
      individualCommissionRates?: {
        [barberId: string]: number; // Per-barber commission percentage
      };

      // Product commission (when barber sells a product)
      productCommissionEnabled: boolean;
      productCommissionRate: number; // e.g., 10%

      // Tips handling
      tipsIncludedInReports: boolean;
      tipsSplitWithShop: boolean; // Rare, but some shops split tips
      tipsShopPercentage: number; // If split, shop percentage
    };

    // Report Automation
    automation: {
      dailyEmailEnabled: boolean;
      weeklyEmailEnabled: boolean;
      weeklyEmailDay: 0 | 1 | 2 | 3 | 4 | 5 | 6; // Day of week to send (e.g., Monday)
      recipients: string[]; // Email addresses
    };
  };

  // 🔒 STATION LOCK (Jose 2026-09-24): the shared-iPad operator model auto-returns to the PIN pad
  // after this many minutes of no activity, so the next person taps in. 0 = OFF (manual Lock Screen
  // only). Read live by PinLockContext's idle timer. Only takes effect where the operator model is
  // active (staging now; prod with the security one-shot).
  stationLock?: {
    autoLockMinutes: number; // 0 = off, else 1/2/5/10/15…
  };

  // Development Mode
  devMode: boolean; // Allow past appointments, extra logging, etc.

  // Metadata
  updatedAt: Date;
  updatedBy: string;
}

class ShopSettingsService {
  private static instance: ShopSettingsService;
  private cachedSettings: ShopSettings | null = null;
  private listeners: ((settings: ShopSettings) => void)[] = [];
  private unsubscribe: (() => void) | null = null;

  private constructor() {
    // RESILIENCE (Jose 2026-10-29 audit): the settings snapshot is set up lazily on first
    // subscribe(), which can happen BEFORE Firebase auth is ready → a pre-auth denied read would
    // leave stale/default settings for the whole session with no self-heal. Re-establish the live
    // listener once auth is ready IF someone is actually listening. See shiftService fix.
    try {
      onAuthStateChanged(auth, (u) => {
        if (u && this.listeners.length > 0) this.establishListener();
      });
    } catch { /* ignore */ }
  }

  static getInstance(): ShopSettingsService {
    if (!ShopSettingsService.instance) {
      ShopSettingsService.instance = new ShopSettingsService();
    }
    return ShopSettingsService.instance;
  }

  /**
   * Get default shop settings
   */
  getDefaultSettings(): ShopSettings {
    const defaultSettings: ShopSettings = {
      shopName: 'SLIK POS Barbershop',
      shopAddress: '',
      shopPhone: '',
      shopEmail: '',
      shopTimezone: 'America/New_York',

      businessHours: {
        monday: { open: '09:00', close: '20:00', closed: false },
        tuesday: { open: '09:00', close: '20:00', closed: false },
        wednesday: { open: '09:00', close: '20:00', closed: false },
        thursday: { open: '09:00', close: '20:00', closed: false },
        friday: { open: '09:00', close: '20:00', closed: false },
        saturday: { open: '09:00', close: '20:00', closed: false },
        sunday: { open: '10:00', close: '18:00', closed: false }
      },

      bookingSettings: {
        minAdvanceBookingMinutes: 30,
        maxAdvanceBookingDays: 90,
        defaultAppointmentDuration: 30,
        slotInterval: 15, // Default to 15 minutes — standard barbershop intervals
        allowOverlappingBookings: false,
        requireDepositForNewClients: false,
        depositAmount: 0,
        emergencyBookingFees: {
          highPriorityFee: 37.50,
          criticalFee: 50.00
        }
      },

      // Per-shop booking rules — seed with the shared defaults (back-to-back ON,
      // no-show occupies, 45-min new client, 2-strike no-show block). A shop tunes
      // these from Settings → Booking Rules. See shared/bookingRules.ts.
      bookingRules: { ...DEFAULT_BOOKING_RULES },

      calendarSettings: {
        startHour: 9,
        endHour: 20,
        showWeekends: true,
        defaultView: 'day',
        showCancelledAppointments: false
      },

      notificationSettings: {
        sendAppointmentReminders: true,
        reminderHoursBefore: 24,
        sendConfirmationEmails: true,
        sendCancellationEmails: true,
        notifyStaffOfNewBookings: true
      },

      paymentSettings: {
        acceptCash: true,
        acceptCard: true,
        acceptSLIKCoins: true,
        taxRate: 0,
        tipSuggestions: [15, 18, 20, 25]
      },

      // Cancellation & no-show policy (Jose 2026-09-07). Reasons mirror the Fresha-style dropdown;
      // notify-on-cancel defaults OFF per Jose. Late-cancel→no-show toggle is in bookingRules.
      cancellationSettings: {
        reasons: [
          { id: 'client_request', label: 'Client requested', enabled: true, order: 1 },
          { id: 'reschedule', label: 'Rescheduled', enabled: true, order: 2 },
          { id: 'client_no_show', label: 'Client did not show', enabled: true, order: 3 },
          { id: 'barber_unavailable', label: 'Barber unavailable', enabled: true, order: 4 },
          { id: 'shop_closure', label: 'Shop closed / emergency', enabled: true, order: 5 },
          { id: 'duplicate', label: 'Duplicate booking', enabled: true, order: 6 },
          { id: 'other', label: 'Other', enabled: true, order: 7 },
        ],
        notifyClientOnCancelDefault: false,
      },

      // Client-facing notification switches (Jose 2026-09-07) — no-show on by default (cancel is
      // governed by the cancel modal toggle, default off via cancellationSettings above).
      clientNotifications: {
        onNoShow: true,
      },

      // 🎨 Default theme = SLIK "Warm Copper" + the tone mapping Jose picked 2026-09-07
      // (cards dark · heroes light · buttons light/medium · barber tiles medium).
      theme: {
        brand: { primary: '#C0824A', hero: '#6f4a30', accent: '#2f4a7a' },
        tones: {
          cards: 'dark', heroes: 'light', buttonsPrimary: 'light', buttonsSecondary: 'medium', barberTiles: 'medium',
          portalHeroes: 'medium', portalButtons: 'light', portalCards: 'dark',
          publicHeroes: 'medium', publicButtons: 'light', publicBarberTiles: 'medium',
        },
        intensity: 0.6,
      },

      staffSettings: {
        requireBPIMinimum: 70,
        autoAssignAppointments: false,
        showStaffPerformanceToCustomers: true
      },

      features: {
        enableEmergencyBooking: true,
        enableAIScheduling: false, // Premium feature
        enableLoyaltyProgram: true,
        enableWaitlist: true,
        enableRecurringAppointments: false,
        enableMultiLocationSupport: false,
        enableGiftCards: true
      },

      giftCardSettings: {
        enabled: true,
        defaultExpirationMonths: 12,
        allowCustomAmounts: true,
        minAmount: 25,
        maxAmount: 500,
        presetAmounts: [25, 50, 75, 100, 150, 200],
        templates: [
          {
            id: 'default',
            name: 'SLIK Classic',
            imageUrl: '/assets/gift-card-templates/slik-classic.png',
            description: 'Classic SLIK Barber Lounge gift card',
            season: 'default',
            active: true
          }
        ]
      },

      // Default Discount Reasons — now with a type ($ / % / comp) + value (Jose 2026-08-09) so they
      // apply straight from a dropdown. Admins tune these in Settings.
      discountReasons: [
        { id: 'complimentary', label: 'Complimentary', enabled: true, order: 1, type: 'comp' as const, value: 100 },
        { id: 'loyalty', label: 'Loyalty Reward', enabled: true, order: 2, type: 'percent' as const, value: 10 },
        { id: 'family_member', label: 'Family/Friend Discount', enabled: true, order: 3, type: 'percent' as const, value: 15 },
        { id: 'ten_off', label: '$10 Off', enabled: true, order: 4, type: 'fixed' as const, value: 10 },
        { id: 'five_off', label: '$5 Off', enabled: true, order: 5, type: 'fixed' as const, value: 5 },
        { id: 'service_issue', label: 'Service Issue', enabled: true, order: 6, type: 'comp' as const, value: 100 },
        { id: 'unsatisfied_client', label: 'Unsatisfied Client', enabled: true, order: 7, type: 'percent' as const, value: 50 },
        { id: 'promotion', label: 'Promotion/Special', enabled: true, order: 8, type: 'percent' as const, value: 20 },
        { id: 'grandfather_pricing', label: 'Grandfather Pricing', enabled: true, order: 9, type: 'fixed' as const, value: 0 },
        { id: 'compensation', label: 'Compensation for Error', enabled: true, order: 10, type: 'comp' as const, value: 100 },
        { id: 'other', label: 'Other', enabled: true, order: 11, type: 'fixed' as const, value: 0 }
      ],

      // 📊 DEFAULT REPORTING SETTINGS
      reporting: {
        payroll: {
          frequency: 'weekly',
          commissionType: 'flat',
          flatCommissionRate: 60, // 60/40 split (barber/shop)
          individualCommissionRates: {},
          productCommissionEnabled: true,
          productCommissionRate: 10, // 10% of product sales
          tipsIncludedInReports: true,
          tipsSplitWithShop: false,
          tipsShopPercentage: 0
        },
        automation: {
          dailyEmailEnabled: false,
          weeklyEmailEnabled: false,
          weeklyEmailDay: 1, // Monday
          recipients: []
        }
      },

      stationLock: {
        autoLockMinutes: 1 // default: return to the PIN pad after 1 min idle
      },

      devMode: false,

      updatedAt: new Date(),
      updatedBy: 'system'
    };
    return defaultSettings;
  }

  /**
   * Initialize settings in Firestore if they don't exist
   */
  async initializeSettings(): Promise<void> {
    const docRef = doc(db, 'settings', 'shop');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.log('📝 Initializing shop settings with defaults...');
      const defaultSettings = this.getDefaultSettings();
      await setDoc(docRef, defaultSettings);
      console.log('✅ Shop settings initialized');
    } else {
      console.log('✅ Shop settings already exist');
    }

    // SCHEDULE GLOBALIZATION (2026-08-01): ensure the PUBLIC hours mirror exists
    // for already-provisioned shops too (not just fresh installs), so customer
    // surfaces have a source to read even before the next Settings save.
    try {
      const current = await this.getSettings();
      await this.mirrorPublicHours(current.businessHours, current.specialHours);
    } catch (err) {
      console.warn('⚠️ Could not seed public hours mirror on init:', err);
    }
  }

  /**
   * Get current shop settings from Firestore
   */
  async getSettings(): Promise<ShopSettings> {
    // Return cached if available
    if (this.cachedSettings) {
      return this.cachedSettings;
    }

    const docRef = doc(db, 'settings', 'shop');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Initialize with defaults if not found
      await this.initializeSettings();
      return this.getDefaultSettings();
    }

    const settings = {
      ...docSnap.data(),
      updatedAt: docSnap.data().updatedAt?.toDate() || new Date()
    } as ShopSettings;

    this.cachedSettings = settings;
    return settings;
  }

  /**
   * Update shop settings
   */
  async updateSettings(updates: Partial<ShopSettings>, updatedBy: string = 'admin'): Promise<void> {
    const docRef = doc(db, 'settings', 'shop');

    const updateData = {
      ...updates,
      updatedAt: new Date(),
      updatedBy
    };

    await updateDoc(docRef, updateData);

    // Clear cache to force reload
    this.cachedSettings = null;

    // Notify listeners
    const newSettings = await this.getSettings();
    this.notifyListeners(newSettings);

    // SCHEDULE GLOBALIZATION (2026-08-01): keep the public hours mirror in sync
    // so customer-facing surfaces read the owner's real hours. See mirrorPublicHours.
    await this.mirrorPublicHours(newSettings.businessHours, newSettings.specialHours);

    // BOOKING RULES (2026-08-07): if a save touched the per-shop booking rules,
    // mirror them to publicConfig/bookingRules so the customer-facing surfaces
    // (customer Book Now, public guest app) compute availability the SAME way.
    if ('bookingRules' in updates) {
      await this.mirrorPublicBookingRules(mergeBookingRules(newSettings.bookingRules));
    }

    // THEME (2026-09-08, Phase 3): mirror the owner's theme to publicConfig/theme so the
    // customer-portal + public-booking apps (guests can't read staff-only settings/shop)
    // pick up the brand color + per-element tones. Same mirror pattern as hours/bookingRules.
    if ('theme' in updates && newSettings.theme) {
      await this.mirrorPublicTheme(newSettings.theme);
    }

    console.log('✅ Shop settings updated');
  }

  /**
   * THEME MIRROR (Phase 3) — write the shop's theme to a PUBLIC-readable doc
   * (publicConfig/theme) so the customer/public apps reflect the owner's look.
   * settings/shop is staff-only; publicConfig is public-read + staff-write.
   */
  private async mirrorPublicTheme(theme: ThemeConfig): Promise<void> {
    try {
      await setDoc(doc(db, 'publicConfig', 'theme'), { theme, updatedAt: new Date() }, { merge: true });
      console.log('🎨 Public theme mirror updated');
    } catch (err) {
      console.warn('⚠️ Could not mirror public theme:', err);
    }
  }

  /**
   * Get this shop's booking rules, defaults filled in. Every availability/booking
   * brain should read from here so the whole app obeys the shop's own parameters.
   * BEHAVIOR-PIN: project-slik-pos-booking-rules-config-2026-08-07
   */
  async getBookingRules(): Promise<BookingRules> {
    const settings = await this.getSettings();
    return mergeBookingRules(settings.bookingRules);
  }

  /**
   * Update this shop's booking rules (partial merge over current) + mirror to
   * the public-readable doc so customer surfaces stay in lockstep.
   */
  async updateBookingRules(rules: Partial<BookingRules>, updatedBy: string = 'admin'): Promise<void> {
    const current = await this.getBookingRules();
    const merged = mergeBookingRules({ ...current, ...rules });
    await this.updateSettings({ bookingRules: merged }, updatedBy);
    // updateSettings already mirrors because 'bookingRules' is in updates, but call
    // explicitly too so a direct updateBookingRules never depends on that branch.
    await this.mirrorPublicBookingRules(merged);
  }

  /**
   * BOOKING RULES MIRROR (2026-08-07): write the shop's booking rules to a
   * PUBLIC-readable doc (publicConfig/bookingRules) so every customer-facing
   * surface reads the SAME rules the owner sets. settings/shop is staff-only;
   * publicConfig is public-read + staff-write (firestore.rules). Source of truth
   * stays settings/shop — this is a derived mirror written on every rules save.
   * Mirrors the mirrorPublicHours pattern exactly.
   * BEHAVIOR-PIN: shared/bookingRules.ts / project-slik-pos-booking-rules-config-2026-08-07
   */
  private async mirrorPublicBookingRules(rules: BookingRules): Promise<void> {
    try {
      await setDoc(
        doc(db, 'publicConfig', 'bookingRules'),
        { bookingRules: rules, updatedAt: new Date() },
        { merge: true }
      );
      console.log('🌐 Public booking-rules mirror updated');
    } catch (err) {
      console.warn('⚠️ Could not mirror public booking rules:', err);
    }
  }

  /**
   * SCHEDULE GLOBALIZATION (2026-08-01): mirror the owner's businessHours to a
   * PUBLIC-readable doc (publicConfig/hours) so every customer-facing surface
   * (customer Book Now, public guest app, emergency slot engine) reads the SAME
   * shop hours the owner sets here. settings/shop is staff-only; publicConfig is
   * public-read + staff-write (see firestore.rules). Source of truth stays
   * settings/shop — this is a derived mirror written on every save + init.
   * BEHAVIOR-PIN: EMERGENCY-BOOKING-SPEC.md / project-slik-pos-production-state
   */
  private async mirrorPublicHours(businessHours: ShopSettings['businessHours'] | undefined, specialHours?: SpecialHours): Promise<void> {
    if (!businessHours) return;
    try {
      await setDoc(
        doc(db, 'publicConfig', 'hours'),
        // Mirror the holiday/special-date overrides too, so client booking (portal + public) blocks
        // closed days exactly like the staff calendar. `specialHours ?? {}` so clearing them propagates.
        { businessHours, specialHours: specialHours ?? {}, updatedAt: new Date() },
        { merge: true }
      );
      console.log('🌐 Public hours mirror updated');
    } catch (err) {
      console.warn('⚠️ Could not mirror public hours:', err);
    }
  }

  /**
   * Subscribe to real-time settings changes
   */
  subscribe(callback: (settings: ShopSettings) => void): () => void {
    // Add callback to listeners
    this.listeners.push(callback);

    // Set up Firestore listener if not already listening
    if (!this.unsubscribe) {
      this.establishListener();
    }

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
      if (this.listeners.length === 0 && this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    };
  }

  /**
   * (Re)establish the live settings snapshot. Tears down any prior listener first so an
   * auth-ready re-subscribe never stacks duplicates. Has an error callback so a denied read
   * is logged (never silent) — the auth handler re-runs this once the user signs in.
   */
  private establishListener(): void {
    try { this.unsubscribe?.(); } catch { /* ignore */ }
    const docRef = doc(db, 'settings', 'shop');
    this.unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const settings = {
          ...doc.data(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        } as ShopSettings;

        this.cachedSettings = settings;
        this.notifyListeners(settings);
      }
    }, (error) => {
      console.warn('[shopSettingsService] settings snapshot error (will retry on auth change):', (error as any)?.message || error);
    });
  }

  /**
   * Notify all listeners of settings change
   */
  private notifyListeners(settings: ShopSettings): void {
    this.listeners.forEach(callback => callback(settings));
  }

  /**
   * Check if shop is currently open based on business hours
   */
  isShopOpen(date: Date = new Date()): boolean {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof BusinessHours;
    const settings = this.cachedSettings;

    if (!settings) return true; // Default to open if settings not loaded

    const dayHours = settings.businessHours[dayName];
    if (dayHours.closed) return false;

    const currentTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    return currentTime >= dayHours.open && currentTime <= dayHours.close;
  }

  /**
   * Get business hours for a specific day
   */
  getBusinessHoursForDay(dayName: keyof BusinessHours): { open: string; close: string; closed: boolean } {
    const settings = this.cachedSettings || this.getDefaultSettings();
    return settings.businessHours[dayName];
  }

  /**
   * Check if a date is allowed for booking (respects maxAdvanceBookingDays)
   */
  isDateAllowedForBooking(date: Date): boolean {
    const settings = this.cachedSettings || this.getDefaultSettings();
    const now = new Date();
    const maxDate = new Date(now.getTime() + settings.bookingSettings.maxAdvanceBookingDays * 24 * 60 * 60 * 1000);

    // In dev mode, allow past dates
    if (settings.devMode) {
      return date <= maxDate;
    }

    // In production, only allow future dates within max advance booking days
    return date >= now && date <= maxDate;
  }

  // ============================================================================
  // 📊 REPORTING HELPER METHODS
  // ============================================================================

  // Removed getBarberCommissionRate / setBarberCommissionRate (Jose 2026-09-01) — the legacy
  // reporting.payroll flat/individual commission path. Superseded by the per-barber `commissionMode`
  // ('sliding' = shop Commission & Pay scale · 'flat' = fixed %) resolved via commissionForBarber()
  // in shared/growth.ts, which every reporting/earnings surface now calls (one source of truth).

  /**
   * Get product commission rate
   */
  getProductCommissionRate(): number {
    const settings = this.cachedSettings || this.getDefaultSettings();

    // Add fallback if reporting config doesn't exist yet
    if (!settings.reporting?.payroll) {
      return 10; // Default 10% product commission rate
    }

    if (!settings.reporting.payroll.productCommissionEnabled) {
      return 0;
    }

    return settings.reporting.payroll.productCommissionRate;
  }

  /**
   * Get payroll frequency
   */
  getPayrollFrequency(): 'daily' | 'weekly' | 'biweekly' | 'monthly' {
    const settings = this.cachedSettings || this.getDefaultSettings();
    return settings.reporting?.payroll?.frequency || 'weekly';
  }

  /**
   * Check if tips are included in reports
   */
  areTipsIncludedInReports(): boolean {
    const settings = this.cachedSettings || this.getDefaultSettings();
    return settings.reporting?.payroll?.tipsIncludedInReports ?? true; // Default to true
  }

  /**
   * Update payroll settings
   */
  async updatePayrollSettings(
    payrollSettings: Partial<ShopSettings['reporting']['payroll']>,
    updatedBy: string = 'admin'
  ): Promise<void> {
    const settings = await this.getSettings();
    const defaultSettings = this.getDefaultSettings();

    // Initialize reporting config if it doesn't exist
    const reportingConfig = settings.reporting || defaultSettings.reporting;

    await this.updateSettings({
      reporting: {
        ...reportingConfig,
        payroll: {
          ...reportingConfig.payroll,
          ...payrollSettings
        }
      }
    }, updatedBy);

    console.log('✅ Payroll settings updated');
  }

  /**
   * Update reporting automation settings
   */
  async updateReportAutomation(
    automation: Partial<ShopSettings['reporting']['automation']>,
    updatedBy: string = 'admin'
  ): Promise<void> {
    const settings = await this.getSettings();
    const defaultSettings = this.getDefaultSettings();

    // Initialize reporting config if it doesn't exist
    const reportingConfig = settings.reporting || defaultSettings.reporting;

    await this.updateSettings({
      reporting: {
        ...reportingConfig,
        automation: {
          ...reportingConfig.automation,
          ...automation
        }
      }
    }, updatedBy);

    console.log('✅ Report automation settings updated');
  }

  // ============================================================================
  // 🏷️ DISCOUNT REASONS HELPER METHODS
  // ============================================================================

  /**
   * Get all enabled discount reasons
   */
  async getDiscountReasons(): Promise<{ id: string; label: string; enabled: boolean; order: number; type?: 'comp' | 'percent' | 'fixed'; value?: number }[]> {
    const settings = await this.getSettings();
    const defaultSettings = this.getDefaultSettings();
    const defaults = (defaultSettings.discountReasons ?? [])
      .filter(r => r.enabled).sort((a, b) => a.order - b.order);

    // NEVER-EMPTY (Jose 2026-10-23, "discounts showing up empty again"): `??` only falls back when the
    // field is null/undefined — NOT when it's present-but-empty or all-disabled. On the iPad long-poll a
    // partial/stale settings read can yield exactly that, and the checkout's catch-fallback never fires
    // (no error was thrown) → a blank discount picker. So: use stored reasons when they produce at least
    // one enabled entry; otherwise fall back to the built-in defaults. The picker can never be empty.
    const stored = Array.isArray(settings.discountReasons) ? settings.discountReasons : [];
    const storedEnabled = stored.filter(r => r.enabled).sort((a, b) => a.order - b.order);
    return storedEnabled.length > 0 ? storedEnabled : defaults;
  }

  /**
   * Update discount reasons
   */
  async updateDiscountReasons(
    reasons: { id: string; label: string; enabled: boolean; order: number; type?: 'comp' | 'percent' | 'fixed'; value?: number }[],
    updatedBy: string = 'admin'
  ): Promise<void> {
    await this.updateSettings({
      discountReasons: reasons
    }, updatedBy);

    console.log('✅ Discount reasons updated');
  }

  /**
   * Add a new discount reason
   */
  async addDiscountReason(label: string, updatedBy: string = 'admin'): Promise<void> {
    const settings = await this.getSettings();
    const defaultSettings = this.getDefaultSettings();

    const currentReasons = settings.discountReasons ?? defaultSettings.discountReasons ?? [];

    if (!currentReasons) {
      throw new Error('Unable to load discount reasons');
    }

    const maxOrder = Math.max(...currentReasons.map(r => r.order), 0);

    const newReason = {
      id: label.toLowerCase().replace(/\s+/g, '_'),
      label,
      enabled: true,
      order: maxOrder + 1
    };

    await this.updateDiscountReasons([...currentReasons, newReason], updatedBy);
  }

  // ============================================================================
  // 🔧 GENERIC SETTINGS METHODS (for AI, Emergency Booking, Station Layout, etc.)
  // ============================================================================

  /**
   * Get settings for a specific feature/module type
   * Stored in separate Firestore documents: shopSettings/{type}
   */
  async getSettingsByType<T>(type: string): Promise<T | null> {
    try {
      const docRef = doc(db, 'shopSettings', type);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data() as T;
    } catch (error) {
      console.error(`Failed to get settings for ${type}:`, error);
      return null;
    }
  }

  /**
   * Save settings for a specific feature/module type.
   * Stored in separate Firestore documents: shopSettings/{type} (each type is its OWN doc, so a write
   * here never touches a different type's doc).
   *
   * ⚠️ INVARIANT — this is a FULL OVERWRITE of the type doc, NOT a merge. Every caller MUST pass the
   * COMPLETE object for the type (load current → modify → save whole). Today all callers do: they pass
   * a `mergeXxxConfig(...)` result or an already-complete config (`clean`/`this.config`/`this.settings`),
   * so no keys are lost — and the overwrite correctly drops removed keys. If you add a caller that writes
   * only SOME keys, switch to `setDoc(..., { merge: true })` or you WILL blank the rest. (audited 2026-10-23)
   */
  async saveSettingsByType<T extends object>(type: string, data: T): Promise<void> {
    try {
      const docRef = doc(db, 'shopSettings', type);
      await setDoc(docRef, {
        ...data,
        updatedAt: new Date()
      });
      console.log(`✅ Settings saved for ${type}`);
    } catch (error) {
      console.error(`Failed to save settings for ${type}:`, error);
      throw error;
    }
  }
}

export const shopSettingsService = ShopSettingsService.getInstance();
